import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Search, Edit2, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [q, setQ] = useState("");
  const [qualityFilter, setQualityFilter] = useState("all");
  const [collFilter, setCollFilter] = useState("all");
  const [delId, setDelId] = useState(null);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (qualityFilter !== "all") params.quality_id = qualityFilter;
    if (collFilter !== "all") params.collection_id = collFilter;
    const { data } = await api.get("/admin/products", { params });
    setProducts(data);
  };

  useEffect(() => {
    api.get("/admin/collections").then(({ data }) => setCollections(data));
    api.get("/admin/qualities").then(({ data }) => setQualities(data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, qualityFilter, collFilter]);

  const toggleActive = async (p) => {
    await api.put(`/admin/products/${p.id}`, { active: !p.active });
    toast.success("Güncellendi");
    load();
  };

  const toggleShowcase = async (p) => {
    await api.put(`/admin/products/${p.id}`, { in_collection_showcase: !p.in_collection_showcase });
    toast.success("Vitrin güncellendi");
    load();
  };

  const remove = async () => {
    if (!delId) return;
    await api.delete(`/admin/products/${delId}`);
    toast.success("Ürün silindi");
    setDelId(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="label-tag">Ürün Yönetimi</span>
          <h1 className="font-serif text-4xl mt-2">Ürünler</h1>
        </div>
        <Link to="/admin/urunler/yeni" className="btn-luxe text-xs" data-testid="admin-product-new-btn">
          <Plus size={14} /> Yeni Ürün
        </Link>
      </div>

      <div className="bg-white border border-[#eae5dc] p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655c]" />
          <Input
            placeholder="Ürün adı"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="admin-product-search"
          />
        </div>
        <Select value={qualityFilter} onValueChange={setQualityFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="admin-product-quality-filter"><SelectValue placeholder="Kalite" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kaliteler</SelectItem>
            {qualities.map((qq) => <SelectItem key={qq.id} value={qq.id}>{qq.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={collFilter} onValueChange={setCollFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="admin-product-coll-filter"><SelectValue placeholder="Koleksiyon" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Koleksiyonlar</SelectItem>
            {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#eae5dc] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="bg-[#f7f5f0] text-xs uppercase tracking-wider text-[#6b655c]">
              <th className="text-left py-3 px-4">Ürün</th>
              <th className="text-left py-3 px-4">Kalite</th>
              <th className="text-left py-3 px-4">Koleksiyon</th>
              <th className="text-center py-3 px-4">Varyant</th>
              <th className="text-center py-3 px-4">Stok</th>
              <th className="text-center py-3 px-4">Vitrin</th>
              <th className="text-center py-3 px-4">Aktif</th>
              <th className="text-right py-3 px-4">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && (
              <tr><td colSpan={8} className="py-12 text-center text-[#6b655c]">Ürün yok</td></tr>
            )}
            {products.map((p) => (
              <tr key={p.id} className="border-t border-[#f0ebe2]">
                <td className="py-3 px-4 flex items-center gap-3">
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-10 h-10 object-cover" />}
                  <span className="font-medium">{p.name}</span>
                </td>
                <td className="py-3 px-4 text-[#1a1a1a]"><span className="text-xs uppercase tracking-wider text-[#c8a97e]">{p.quality_name || "—"}</span></td>
                <td className="py-3 px-4 text-[#6b655c]">{p.collection_name || "—"}</td>
                <td className="py-3 px-4 text-center">{p.variants_count}</td>
                <td className="py-3 px-4 text-center">{p.total_stock}</td>
                <td className="py-3 px-4 text-center">
                  <Switch checked={p.in_collection_showcase} onCheckedChange={() => toggleShowcase(p)} data-testid={`admin-product-showcase-${p.id}`} />
                </td>
                <td className="py-3 px-4 text-center">
                  <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`admin-product-active-${p.id}`} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex gap-1">
                    <Link to={`/admin/urunler/${p.id}`} className="p-2 hover:bg-[#f7f5f0]" data-testid={`admin-product-edit-${p.id}`}><Edit2 size={14} /></Link>
                    <button onClick={() => setDelId(p.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`admin-product-del-${p.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ürünü silmek istiyor musunuz?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz. Ürün ve varyantları kalıcı olarak silinir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-product-confirm-del-btn">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
